
import { DayOfWeek } from '@/models/Schedule';
import { addMinutes, format, parse } from 'date-fns';

interface RecurringTimeSlotData {
  selectedDays: string[];
  recurringStartTime: string;
  recurringEndTime: string;
  recurringInterval: string;
  slotDuration: string;
  workoutTypeId: string;
  maxClients: number;
}

interface GeneratedTimeSlot {
  day_of_week: string;
  start_time: string;
  end_time: string;
  workout_type_id: string;
  max_clients: number;
  is_active: boolean;
}

export const generateRecurringTimeSlots = (
  data: RecurringTimeSlotData
): GeneratedTimeSlot[] => {
  const slots: GeneratedTimeSlot[] = [];
  
  // Parse times
  const baseDate = new Date(2000, 0, 1); // Use a fixed date for time calculations
  const startTime = parse(data.recurringStartTime, 'HH:mm', baseDate);
  const endTime = parse(data.recurringEndTime, 'HH:mm', baseDate);
  const intervalMinutes = parseInt(data.recurringInterval);
  const durationMinutes = parseInt(data.slotDuration);

  // Generate slots for each selected day
  data.selectedDays.forEach((day) => {
    let currentTime = startTime;
    
    while (currentTime < endTime) {
      const slotEndTime = addMinutes(currentTime, durationMinutes);
      
      // Check if the slot would end after the recurring end time
      if (slotEndTime > endTime) {
        break;
      }
      
      slots.push({
        day_of_week: day,
        start_time: format(currentTime, 'HH:mm'),
        end_time: format(slotEndTime, 'HH:mm'),
        workout_type_id: data.workoutTypeId,
        max_clients: data.maxClients,
        is_active: true,
      });
      
      // Move to next interval
      currentTime = addMinutes(currentTime, intervalMinutes);
    }
  });

  return slots;
};

export const validateRecurringSettings = (
  startTime: string,
  endTime: string,
  interval: string,
  duration: string
): { isValid: boolean; error?: string } => {
  const baseDate = new Date(2000, 0, 1);
  const start = parse(startTime, 'HH:mm', baseDate);
  const end = parse(endTime, 'HH:mm', baseDate);
  const intervalMinutes = parseInt(interval);
  const durationMinutes = parseInt(duration);

  // Check if end time is after start time
  if (end <= start) {
    return { isValid: false, error: 'End time must be after start time' };
  }

  // Check if duration is not greater than interval
  if (durationMinutes > intervalMinutes) {
    return { isValid: false, error: 'Slot duration cannot be greater than interval' };
  }

  // Check if at least one slot can be created
  const firstSlotEnd = addMinutes(start, durationMinutes);
  if (firstSlotEnd > end) {
    return { isValid: false, error: 'Time range too small for the specified duration' };
  }

  return { isValid: true };
};
